/**
 * "Best Time to Visit" pages — evergreen SEO pages for high-volume travel queries.
 *
 * Provider registers slug entries for generateStaticParams.
 * Renderer delegates to BestTimeSeoSection for page rendering.
 */

import { getEventsForCountry, getCountryByCode } from './data-source';
import { registerEvergreenProvider, type EvergreenSlugEntry, getLanguagesForCountry, getPhraseEntry, getTemplateStr, getCountryNameEntry } from './i18n';
import { registerEvergreenRenderer } from './evergreen-pages';
import { registerEvergreenReadiness } from './slug-evergreen';
import { getBestTimeRows, isBestTimeLoaded } from './db-best-time';
import { CALENDAR_YEAR } from './config';
import { SITE_NAME } from './brand';
import { BestTimeSeoSection } from '@/components/seo/BestTimeSeoSection';
import type { CountryCode } from './types';

const YEAR = CALENDAR_YEAR;

// ---------------------------------------------------------------------------
// Season data types
// ---------------------------------------------------------------------------

export interface SeasonDef {
  name: string;
  months: number[]; // 1-indexed
  tempRange: string;
  weather: string;
  crowds: 'Low' | 'Medium' | 'High';
  prices: 'Low' | 'Medium' | 'High';
  verdict: string;
}

export interface MonthInfo {
  month: number; // 1-indexed
  season: string;
  highlight: string;
}

export interface BestTimeDef {
  slug: string;
  displayName: string;
  countryCode: CountryCode;
  metaTitle: string;
  metaDescription: string;
  seasons: SeasonDef[];
  months: MonthInfo[];
  bestMonths: number[]; // peak season months (1-indexed)
  faq: Array<{ question: string; answer: string }>;
}

// ---------------------------------------------------------------------------
// Definitions
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Best-time pages — loaded from almanak.best_time (pju1). Rows carry
// {year}/{site} placeholders in meta fields; resolve them here. Returns [] until
// preloadBestTime() has run; the readiness check below gates the evergreen
// registry from caching before that.
// ---------------------------------------------------------------------------

function resolveTpl(s: string): string {
  return s.split('{year}').join(String(YEAR)).split('{site}').join(SITE_NAME);
}

function getBestTimePages(): BestTimeDef[] {
  return getBestTimeRows().map(row => ({
    slug: row.slug,
    displayName: row.display_name,
    countryCode: row.country_code as CountryCode,
    metaTitle: resolveTpl(row.meta_title),
    metaDescription: resolveTpl(row.meta_description),
    seasons: row.seasons as SeasonDef[],
    months: row.months as MonthInfo[],
    bestMonths: row.best_months ?? [],
    faq: row.faq as BestTimeDef['faq'],
  }));
}

// ---------------------------------------------------------------------------
// Evergreen provider
// ---------------------------------------------------------------------------

function provideBestTimeSlugs(): EvergreenSlugEntry[] {
  const entries: EvergreenSlugEntry[] = [];

  for (const def of getBestTimePages()) {
    const langs = getLanguagesForCountry(def.countryCode);
    const country = getCountryByCode(def.countryCode);
    const countryFlag = country?.flag ?? '';
    const entityId = `best-time:${def.countryCode.toLowerCase()}`;
    // Extract destination slug suffix from English slug (e.g. 'thailand' from 'best-time-to-visit-thailand')
    const destSlug = def.slug.replace('best-time-to-visit-', '');

    const seenSlugs = new Set<string>();
    for (const lang of langs) {
      const bestTimePhrase = getPhraseEntry('best_time', lang);
      const slug = `${bestTimePhrase.slug}-${destSlug}`;

      // Skip languages whose phrase falls back to English, producing a duplicate slug
      if (seenSlugs.has(slug)) continue;
      seenSlugs.add(slug);

      // Localize the country name on non-English pages. Only when the destination
      // IS the whole country (its English label matches the country name); sub-
      // country destinations like Dubai keep their curated label. (almanak-kg1c)
      const enCountryName = getCountryNameEntry(def.countryCode, 'en').display;
      const localizedName = def.displayName === enCountryName
        ? getCountryNameEntry(def.countryCode, lang).display
        : def.displayName;

      const tplVars = {
        country: localizedName,
        year: YEAR,
        siteName: SITE_NAME,
      };

      const rawTitle = getTemplateStr('best_time_title', lang, tplVars);
      const pageTitle = rawTitle
        ? (rawTitle.includes(SITE_NAME) ? rawTitle : `${rawTitle} | ${SITE_NAME}`)
        : def.metaTitle;
      const pageDescription = getTemplateStr('best_time_desc', lang, tplVars)
        || def.metaDescription;

      entries.push({
        pageType: 'best-time',
        slug,
        lang,
        entityId,
        pageTitle,
        pageDescription,
        h2Title: `${bestTimePhrase.display} ${localizedName}`,
        data: {
          displayName: localizedName,
          countryCode: def.countryCode,
          countryFlag,
          bestTimeSlug: def.slug,
        },
      });
    }
  }

  return entries;
}

registerEvergreenProvider(provideBestTimeSlugs);
// Gate the evergreen registry from caching until best-time pages are loaded from
// the DB (preloadBestTime runs inside preloadConfig). Refs: almanak-pju1.
registerEvergreenReadiness(isBestTimeLoaded);

// ---------------------------------------------------------------------------
// Evergreen renderer
// ---------------------------------------------------------------------------

registerEvergreenRenderer('best-time', (entry, topPages) => {
  const slug = entry.data.bestTimeSlug as string;
  const def = getBestTimePages().find(p => p.slug === slug);
  if (!def) return null;
  // Find sibling best-time pages sharing the same countryCode (e.g. Bali ↔ Indonesia)
  const siblings = getBestTimePages()
    .filter(p => p.countryCode === def.countryCode && p.slug !== def.slug)
    .map(p => ({ displayName: p.displayName, slug: p.slug }));
  return BestTimeSeoSection({ def, entry, siblings });
});

// ---------------------------------------------------------------------------
// Export for component use
// ---------------------------------------------------------------------------

export function getBestTimeDef(slug: string): BestTimeDef | undefined {
  return getBestTimePages().find(p => p.slug === slug);
}

/** Reverse lookup: country code → best-time page slug */
export function getBestTimeSlugForCountry(code: CountryCode): string | null {
  const def = getBestTimePages().findLast(p => p.countryCode === code);
  return def ? def.slug : null;
}
